import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { CAPTURE_WIRED_STAGES, STEP_DEFS, stageTemplateLock } from "../../lib/journey";
import {
  useTenantJourneyStages,
  useSaveTenantJourneyStages,
  useTenantTemplates,
  useSaveTenantTemplates,
} from "../../lib/api";
import TemplateFields from "./TemplateFields";
import {
  FieldError,
  FieldLabel,
  GhostButton,
  PrimaryButton,
  SegmentButton,
  Toggle,
  ToggleRow,
  cardClass,
  inputClass,
  textareaClass,
} from "./primitives";

// group_delete_delay_minutes is stored in minutes (same "one unit, converted
// at the edges" rule as post_delivery_feedback's own delay_minutes); the
// editor lets the brand pick whichever unit reads naturally — minutes for a
// quick test, days for the real production wait — and converts at the edge.
const GROUP_DELETE_DEFAULT_MINUTES = 10080; // 7 days
const GROUP_DELETE_MAX_MINUTES = 129600; // 90 days — matches the server-side cap
const GROUP_DELETE_UNITS = [
  { key: "minutes", label: "minutes", perMinute: 1 },
  { key: "hours", label: "hours", perMinute: 60 },
  { key: "days", label: "days", perMinute: 1440 },
];
const unitFor = (minutes) => {
  const m = Number(minutes) || 0;
  if (m > 0 && m % 1440 === 0) return "days";
  if (m > 0 && m % 60 === 0) return "hours";
  return "minutes";
};
const valueInUnit = (minutes, unitKey) => {
  const perMinute = GROUP_DELETE_UNITS.find((u) => u.key === unitKey).perMinute;
  return Math.round((Number(minutes) || 0) / perMinute);
};
const toMinutes = (value, unitKey) => {
  const perMinute = GROUP_DELETE_UNITS.find((u) => u.key === unitKey).perMinute;
  return Math.min(GROUP_DELETE_MAX_MINUTES, Math.max(0, Number(value) || 0) * perMinute);
};

const NO_STAGE = "__no_stage__";

// "Feedback" (feedback / feedback_cia / feedback_nps) is redundant with Pre
// Delivery Feedback and Post Delivery Feedback here — hidden from this list
// only. The stage itself and its templates are untouched.
const HIDDEN_FROM_LIST = new Set(["feedback"]);

// Stages in the Hyundai Pre Delivery auto-chain: seconds after the previous
// event before this stage's message is sent.
const CHAIN_DELAY_DEFAULTS = {
  pre_delivery_feedback: 4,
  next_service_due: 0,
  post_delivery_feedback: 5,
};

const keywordsToText = (list) => (list || []).join("\n");
const textToKeywords = (text) =>
  (text || "")
    .split("\n")
    .map((k) => k.trim())
    .filter(Boolean);

// Merges the stored overlay onto STEP_DEFS so the editor always shows every
// stage, including ones this tenant has never touched.
function toEditorRows(saved, keywordDefaults) {
  const byKey = new Map((saved || []).map((s) => [s.stage_key, s]));
  const rows = STEP_DEFS.filter((d) => !HIDDEN_FROM_LIST.has(d.key)).map((d, i) => {
    const s = byKey.get(d.key);
    let delay = null;
    if (d.key in CHAIN_DELAY_DEFAULTS) {
      delay =
        typeof s?.auto_send_delay_seconds === "number"
          ? s.auto_send_delay_seconds
          : CHAIN_DELAY_DEFAULTS[d.key];
    }
    return {
      stage_key: d.key,
      defaultLabel: d.label,
      label: s?.label || d.label,
      is_visible: s ? s.is_visible !== false : true,
      sort_order: typeof s?.sort_order === "number" ? s.sort_order : i,
      auto_send_delay_seconds: delay,
      capture_from_whatsapp: s?.capture_from_whatsapp ?? false,
      keywordsText: keywordsToText(s?.capture_keywords?.length ? s.capture_keywords : keywordDefaults[d.key]),
    };
  });
  return rows.sort((a, b) => a.sort_order - b.sort_order);
}

const toTemplateRows = (data) => (data?.rows || []).map((r) => ({ ...r }));

function Chevron({ up }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d={up ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"} />
    </svg>
  );
}

function EyeIcon({ color }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function StageListRow({ row, index, total, selected, onSelect, onMove, onToggleVisible }) {
  return (
    <div
      className="flex items-center gap-[10px] border-b border-[#F0F1F3] pl-[10px] pr-3"
      style={{ background: selected ? "#EFF5FC" : "transparent", opacity: row.is_visible ? 1 : 0.45 }}
    >
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          aria-label={`Move ${row.label} up`}
          className="cursor-pointer text-[#9CA3AF] hover:text-[#111827] disabled:cursor-default disabled:opacity-30"
        >
          <Chevron up />
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={index === total - 1}
          aria-label={`Move ${row.label} down`}
          className="cursor-pointer text-[#9CA3AF] hover:text-[#111827] disabled:cursor-default disabled:opacity-30"
        >
          <Chevron />
        </button>
      </div>
      <button
        type="button"
        onClick={onSelect}
        className="flex h-[46px] min-w-0 flex-1 cursor-pointer items-center gap-3 border-0 bg-transparent p-0 text-left"
      >
        <span
          className="flex h-[22px] w-[22px] flex-[0_0_22px] items-center justify-center rounded-full text-[11px] font-semibold"
          style={{ background: selected ? "#2667B5" : "#EFF5FC", color: selected ? "#FFFFFF" : "#2667B5" }}
        >
          {index + 1}
        </span>
        <span
          className="truncate text-[14px] font-medium"
          style={{ color: selected ? "#1F5596" : "#111827" }}
        >
          {row.label}
        </span>
      </button>
      <button
        type="button"
        onClick={onToggleVisible}
        aria-label={row.is_visible ? `Hide ${row.label}` : `Show ${row.label}`}
        className="flex h-7 w-7 flex-none cursor-pointer items-center justify-center rounded-lg border border-transparent bg-transparent hover:bg-[#EFF5FC]"
      >
        <EyeIcon color={row.is_visible ? "#2667B5" : "#9CA3AF"} />
      </button>
    </div>
  );
}

function NoStageRow({ selected, onSelect }) {
  return (
    <div
      className="flex items-center gap-[10px] pl-[14px] pr-3"
      style={{ background: selected ? "#EFF5FC" : "transparent" }}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex h-[46px] min-w-0 flex-1 cursor-pointer items-center gap-3 border-0 bg-transparent p-0 text-left"
      >
        <span
          className="flex h-[22px] w-[22px] flex-[0_0_22px] items-center justify-center rounded-full"
          style={{ background: selected ? "#2667B5" : "#EFF5FC", color: selected ? "#FFFFFF" : "#2667B5" }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
        </span>
        <span className="truncate text-[14px] font-medium" style={{ color: selected ? "#1F5596" : "#111827" }}>
          Not tied to a stage
        </span>
      </button>
    </div>
  );
}

// The Communication block for every template that belongs to `filterKey`.
function TemplateList({ templates, filterKey, lock, errors, onPatch }) {
  const match = filterKey === NO_STAGE ? (r) => !r.stage : (r) => r.stage === filterKey;
  const rows = templates.filter(match);
  if (!rows.length) return null;
  return (
    <div className="flex flex-col gap-5">
      {rows.map((r) => (
        <div key={r.template_key}>
          <FieldLabel>{rows.length > 1 ? r.label : "Communication"}</FieldLabel>
          <TemplateFields
            row={r}
            onChange={(p) => onPatch(r.template_key, p)}
            locked={lock.locked}
            lockedReason={lock.reason}
            error={errors[r.template_key]}
          />
        </div>
      ))}
    </div>
  );
}

export default function StageEditor({ tenantId }) {
  const { data, isLoading } = useTenantJourneyStages(tenantId);
  const { data: templatesData, isLoading: templatesLoading } = useTenantTemplates(tenantId);
  const saveStages = useSaveTenantJourneyStages(tenantId);
  const saveTemplates = useSaveTenantTemplates(tenantId);
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [errors, setErrors] = useState({ label: {}, templates: {} });

  const keywordDefaults = useMemo(() => data?.keyword_defaults || {}, [data]);
  const baselineRows = useMemo(
    () => (data ? toEditorRows(data.rows, keywordDefaults) : []),
    [data, keywordDefaults],
  );
  const baselineTemplates = useMemo(() => toTemplateRows(templatesData), [templatesData]);

  useEffect(() => setRows(baselineRows), [baselineRows]);
  useEffect(() => setTemplates(baselineTemplates), [baselineTemplates]);

  useEffect(() => {
    if (!selectedKey && rows.length) setSelectedKey(rows[0].stage_key);
  }, [rows, selectedKey]);

  const hasNoStageTemplates = templates.some((r) => !r.stage);

  const baselineTemplatesByKey = useMemo(
    () => new Map(baselineTemplates.map((t) => [t.template_key, JSON.stringify(t)])),
    [baselineTemplates],
  );
  const changedTemplates = templates.filter(
    (t) => baselineTemplatesByKey.get(t.template_key) !== JSON.stringify(t),
  );
  const dirty = JSON.stringify(rows) !== JSON.stringify(baselineRows) || changedTemplates.length > 0;

  const captureOffered = (key) => CAPTURE_WIRED_STAGES.has(key);

  const selectedIndex = rows.findIndex((r) => r.stage_key === selectedKey);
  const selectedRow = selectedIndex >= 0 ? rows[selectedIndex] : null;
  const onNoStage = selectedKey === NO_STAGE;
  const pdfTemplateRow = templates.find((t) => t.template_key === "post_delivery_feedback");
  const escTemplateRow = templates.find((t) => t.template_key === "workshop_contacts");

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
  };

  const setField = (key, patch) =>
    setRows((rs) => rs.map((r) => (r.stage_key === key ? { ...r, ...patch } : r)));

  const patchTemplate = (key, patch) =>
    setTemplates((ts) => ts.map((t) => (t.template_key === key ? { ...t, ...patch } : t)));

  const validate = () => {
    const label = {};
    rows.forEach((r) => {
      if (!r.label.trim()) label[r.stage_key] = "Enter a stage name.";
    });
    const tpl = {};
    changedTemplates.forEach((t) => {
      if (t.send_mode === "direct" && !(t.body || "").trim()) tpl[t.template_key] = "Enter the message text.";
    });
    setErrors({ label, templates: tpl });
    const firstStage =
      Object.keys(label)[0] ||
      templates.find((t) => tpl[t.template_key])?.stage ||
      (Object.keys(tpl).length ? NO_STAGE : null);
    if (firstStage) setSelectedKey(firstStage);
    return !firstStage;
  };

  const onSave = async () => {
    if (!validate()) return;
    try {
      await saveStages.mutateAsync(
        rows.map((r) => {
          const keywords = textToKeywords(r.keywordsText);
          const matchesDefault =
            keywordsToText(keywords) === keywordsToText(keywordDefaults[r.stage_key]);
          return {
            stage_key: r.stage_key,
            label: r.label.trim(),
            is_visible: r.is_visible,
            auto_send_delay_seconds: r.stage_key in CHAIN_DELAY_DEFAULTS ? r.auto_send_delay_seconds : null,
            capture_from_whatsapp: captureOffered(r.stage_key) ? !!r.capture_from_whatsapp : false,
            capture_keywords: matchesDefault ? null : keywords,
          };
        }),
      );
      if (changedTemplates.length) {
        await saveTemplates.mutateAsync(
          changedTemplates.map((t) => ({
            template_key: t.template_key,
            send_mode: t.send_mode,
            template_name: (t.template_name || "").trim(),
            body: t.body || "",
            enabled: t.enabled,
            config: t.config || null,
          })),
        );
      }
      toast.success("Service journey saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const onDiscard = () => {
    setRows(baselineRows);
    setTemplates(baselineTemplates);
    setErrors({ label: {}, templates: {} });
  };

  if (isLoading || templatesLoading) {
    return (
      <div className="mt-7 flex items-center gap-2 text-[13px] text-[#6B7280]">
        <Loader2 className="animate-spin" size={15} /> Loading
      </div>
    );
  }

  const saving = saveStages.isPending || saveTemplates.isPending;
  const visibleCount = rows.filter((r) => r.is_visible).length;
  const lock = selectedRow ? stageTemplateLock(selectedRow.stage_key, selectedRow) : { locked: false, reason: "" };

  const actions = (
    <div className="mt-[26px] flex items-center gap-3">
      <PrimaryButton onClick={onSave} disabled={saving}>
        {saving && <Loader2 className="animate-spin" size={14} />}
        Save changes
      </PrimaryButton>
      {dirty && <GhostButton onClick={onDiscard}>Discard</GhostButton>}
    </div>
  );

  return (
    <div className="mt-7 grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
      <div className={`overflow-hidden ${cardClass}`}>
        <div className="flex items-center justify-between border-b border-[#F0F1F3] px-4 py-[14px]">
          <span className="text-[14px] font-semibold text-[#111827]">
            {rows.length} {rows.length === 1 ? "stage" : "stages"}
          </span>
          <span className="text-[13px] text-[#6B7280]">{visibleCount} visible</span>
        </div>
        {rows.map((r, i) => (
          <StageListRow
            key={r.stage_key}
            row={r}
            index={i}
            total={rows.length}
            selected={selectedKey === r.stage_key}
            onSelect={() => setSelectedKey(r.stage_key)}
            onMove={(delta) => move(i, delta)}
            onToggleVisible={() => setField(r.stage_key, { is_visible: !r.is_visible })}
          />
        ))}
        {hasNoStageTemplates && (
          <NoStageRow selected={onNoStage} onSelect={() => setSelectedKey(NO_STAGE)} />
        )}
      </div>

      <div className={`${cardClass} p-6`}>
        {onNoStage ? (
          <>
            <h2 className="text-[20px] font-semibold leading-7 text-[#111827]">Not tied to a stage</h2>
            <div className="my-[22px] h-px bg-[#F0F1F3]" />
            <div className="max-w-[560px]">
              <TemplateList
                templates={templates}
                filterKey={NO_STAGE}
                lock={{ locked: false, reason: "" }}
                errors={errors.templates}
                onPatch={patchTemplate}
              />
            </div>
            {actions}
          </>
        ) : selectedRow ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-5">
              <div className="min-w-0">
                <div className="text-[12px] font-medium text-[#6B7280]">
                  Stage {selectedIndex + 1} of {rows.length}
                </div>
                <h2 className="mt-1 text-[20px] font-semibold leading-7 text-[#111827]">{selectedRow.label}</h2>
              </div>
              <div className="flex items-center gap-[10px]">
                <span className="text-[13px] font-medium text-[#4B5563]">Shown to this brand</span>
                <Toggle
                  checked={selectedRow.is_visible}
                  onChange={(on) => setField(selectedRow.stage_key, { is_visible: on })}
                  label="Stage visible"
                />
              </div>
            </div>

            <div className="my-[22px] h-px bg-[#F0F1F3]" />

            <div className="flex max-w-[560px] flex-col gap-5">
              <div>
                <FieldLabel>Stage name</FieldLabel>
                <input
                  type="text"
                  className={inputClass}
                  value={selectedRow.label}
                  onChange={(e) => setField(selectedRow.stage_key, { label: e.target.value })}
                />
                {selectedRow.label !== selectedRow.defaultLabel && (
                  <button
                    type="button"
                    onClick={() => setField(selectedRow.stage_key, { label: selectedRow.defaultLabel })}
                    className="mt-[6px] cursor-pointer text-[12px] font-medium text-[#2667B5] hover:text-[#1F5596]"
                  >
                    Reset to {selectedRow.defaultLabel}
                  </button>
                )}
                <FieldError>{errors.label[selectedRow.stage_key]}</FieldError>
              </div>

              <TemplateList
                templates={templates}
                filterKey={selectedRow.stage_key}
                lock={lock}
                errors={errors.templates}
                onPatch={patchTemplate}
              />

              {captureOffered(selectedRow.stage_key) && (
                <>
                  <ToggleRow
                    title="Capture from WhatsApp"
                    checked={!!selectedRow.capture_from_whatsapp}
                    onChange={(on) => setField(selectedRow.stage_key, { capture_from_whatsapp: on })}
                  />
                  {selectedRow.capture_from_whatsapp && (
                    <div>
                      <FieldLabel>Keywords</FieldLabel>
                      <textarea
                        className={`${textareaClass} min-h-[200px]`}
                        value={selectedRow.keywordsText}
                        placeholder="One per line"
                        onChange={(e) => setField(selectedRow.stage_key, { keywordsText: e.target.value })}
                        aria-label={`Capture keywords for ${selectedRow.label}`}
                      />
                    </div>
                  )}
                </>
              )}

              {selectedRow.stage_key in CHAIN_DELAY_DEFAULTS && (
                <div>
                  <FieldLabel>Wait before sending</FieldLabel>
                  <div className="flex items-center gap-[10px]">
                    <input
                      type="number"
                      min={0}
                      max={86400}
                      className={`${inputClass} !w-[120px]`}
                      value={selectedRow.auto_send_delay_seconds ?? CHAIN_DELAY_DEFAULTS[selectedRow.stage_key]}
                      onChange={(e) =>
                        setField(selectedRow.stage_key, {
                          auto_send_delay_seconds: Math.min(86400, Math.max(0, Number(e.target.value) || 0)),
                        })
                      }
                    />
                    <span className="text-[13px] text-[#6B7280]">seconds</span>
                  </div>
                </div>
              )}

              {selectedRow.stage_key === "post_delivery_feedback" && pdfTemplateRow && (
                <div>
                  <FieldLabel>Send to</FieldLabel>
                  <div className="mb-5 flex flex-wrap gap-2">
                    <SegmentButton
                      active={(pdfTemplateRow.config?.channel || "group") === "group"}
                      onClick={() =>
                        patchTemplate("post_delivery_feedback", {
                          config: { ...(pdfTemplateRow.config || {}), channel: "group" },
                        })
                      }
                    >
                      WhatsApp group
                    </SegmentButton>
                    <SegmentButton
                      active={pdfTemplateRow.config?.channel === "one_on_one"}
                      onClick={() =>
                        patchTemplate("post_delivery_feedback", {
                          config: { ...(pdfTemplateRow.config || {}), channel: "one_on_one" },
                        })
                      }
                    >
                      Customer (1:1)
                    </SegmentButton>
                  </div>

                  <FieldLabel>Delete the WhatsApp group</FieldLabel>
                  <ToggleRow
                    title="Auto delete after Post Delivery Feedback"
                    status=""
                    checked={!!pdfTemplateRow.config?.group_delete_enabled}
                    onChange={(on) =>
                      patchTemplate("post_delivery_feedback", {
                        config: { ...(pdfTemplateRow.config || {}), group_delete_enabled: on },
                      })
                    }
                  />
                  {pdfTemplateRow.config?.group_delete_enabled && (() => {
                    const rawMinutes =
                      pdfTemplateRow.config?.group_delete_delay_minutes ?? GROUP_DELETE_DEFAULT_MINUTES;
                    const unit = unitFor(rawMinutes);
                    const setMinutes = (minutes) =>
                      patchTemplate("post_delivery_feedback", {
                        config: { ...(pdfTemplateRow.config || {}), group_delete_delay_minutes: minutes },
                      });
                    return (
                      <div className="mt-3 flex items-center gap-[10px]">
                        <input
                          type="number"
                          min={0}
                          className={`${inputClass} !w-[120px]`}
                          value={valueInUnit(rawMinutes, unit)}
                          onChange={(e) => setMinutes(toMinutes(e.target.value, unit))}
                          aria-label="How long after Post Delivery Feedback before deleting the group"
                        />
                        <select
                          className={`${inputClass} !w-[120px]`}
                          value={unit}
                          onChange={(e) => setMinutes(toMinutes(valueInUnit(rawMinutes, unit), e.target.value))}
                          aria-label="Unit for the group-delete delay"
                        >
                          {GROUP_DELETE_UNITS.map((u) => (
                            <option key={u.key} value={u.key}>
                              {u.label}
                            </option>
                          ))}
                        </select>
                        <span className="text-[13px] text-[#6B7280]">after it's sent</span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {selectedRow.stage_key === "initial" && escTemplateRow && (() => {
                const sendTiming = escTemplateRow.config?.send_timing;
                const setSendTiming = (value) =>
                  patchTemplate("workshop_contacts", {
                    config: { ...(escTemplateRow.config || {}), send_timing: value },
                  });
                return (
                  <div>
                    <FieldLabel>When to send</FieldLabel>
                    <div className="mb-[10px] flex flex-wrap gap-2">
                      <SegmentButton active={!sendTiming} onClick={() => setSendTiming(undefined)}>
                        Automatic (default)
                      </SegmentButton>
                      <SegmentButton
                        active={sendTiming === "initial"}
                        onClick={() => setSendTiming("initial")}
                      >
                        At group creation
                      </SegmentButton>
                      <SegmentButton
                        active={sendTiming === "after_delivery"}
                        onClick={() => setSendTiming("after_delivery")}
                      >
                        After vehicle delivery
                      </SegmentButton>
                    </div>
                  </div>
                );
              })()}
            </div>

            {actions}
          </>
        ) : null}
      </div>
    </div>
  );
}
